/**
 * registerAllBlocks -- the FULL block set, in the exact order ngAfterViewInit registers it.
 *
 * ORDER IS LOAD-BEARING. `customBlockForPushNewElementToArrayV2` deliberately runs AFTER
 * `EntityVariableFns.customBlockForPushNewElementToArray` because it OVERRIDES it ("value-carrying
 * push"). Register them the other way round and the wrong definition wins, silently.
 *
 * Generated FROM the component's own sequence, not hand-transcribed, and verified call-for-call against
 * it. Three mechanical substitutions only: `this.<Fns>.` -> `<Fns>.` (they are static aliases),
 * `this.customBlockForX()` -> `CoreVariableBlocks.customBlockForX(host)`, and `registerConditionBlocks(this)`
 * -> `(host)`. The `/* DEPRECATED *\/`-commented function-variable registrations are NOT resurrected.
 *
 * NOT INCLUDED, deliberately -- these are the caller's job:
 *   · `AtlasFieldBridge.open / validateEntityChild / attachEntityGuard` -- UI picker + on-attach guards.
 *     The editor wires them to real handlers. A headless caller must decide: no-ops are correct for the
 *     picker (it is a click handler and never fires), but `attachEntityGuard` DOES fire on connect
 *     during a compile, so a headless no-op is a REAL divergence from the editor. Flagged, not hidden.
 *   · `createDefinitionsForFunctions()` -- async and data-driven; call
 *     `registerApiFunctionBlocks(await functionsService.query())` yourself.
 *   · the workspace itself -- the editor injects into a div, a headless runner uses `new Blockly.Workspace()`.
 *
 * See docs/plans/background-generation-pipeline.md and docs/testing/headless-blockly-compiler-tests.md.
 */
import { registerAllMiscBlocks } from './misc-block-definitions';
import { registerPeopleBlocks } from './people-blocks';
import { registerRecordBlocks } from './record-blocks';
import { registerMetaBlocks } from './meta-blocks';
import { registerConditionBlocks } from './condition-blocks';
import { registerProcedureBlock } from './procedure-block';
import * as CoreVariableBlocks from './core-variable-blocks';
import { EntityListPushFns } from './entity-list-push-helpers';
import { QueryFns } from './query-helper';
import { EntityVariableFns } from './entity-variable-healper';
import { SpecialFunctionsFns } from './special-functions-helper';
import { TimeStampFns } from './time-stamp-healper';
import { ConditionalFns } from './conditional-function-helper';
import { ValueFns } from './value-function-helper';
import type { CoreVariableBlocksHost } from './core-variable-blocks';

export function registerAllBlocks(host: CoreVariableBlocksHost) {
  // The function wrapper block. In the editor `newBlocklyFunction` also registers it; re-registering
  // is idempotent (the same definition object is reassigned), so callers need not care which ran.
  registerProcedureBlock();

  // The function-entity block family (13 types) and the three binding fields it read were
  // removed 2026-08-22 after the 25 dependent functions were migrated to the section
  // row-index binding. Do NOT reintroduce. See docs/archive/stale-blocks/README.md.

    registerAllMiscBlocks();
    registerPeopleBlocks();
    registerRecordBlocks();
    registerMetaBlocks();
    // Clicking a VAR dropdown on any block opens Atlas via the SAME action as the side-panel "swap
    // variable" button (openAtlasForBlock, gated on selectedBlockFlag). Deferred so the workspace
    // SELECTED handler — which sets the per-block Atlas context — has run first.
    // On-attach same-entity guard (2026-07-10): the entity setter blocks call this from their
    // setOnChange (on FINISHED_LOADING + a live connect). See docs/blockly/blocks/_VERIFICATION.md.
    // The guard INSTALLER also lives here (not in a helper): block inits call it via the bridge, so the
    // guard logic is centralized in this component and no helper owns it / imports another helper.
    registerConditionBlocks(host);
    // CoreVariableBlocks.customBlockForAndConditionInQuery(host);
    // CoreVariableBlocks.customBlockForTrimDecimals(host);
    // CoreVariableBlocks.customBlockForFormatIsoDate(host);
    // CoreVariableBlocks.customBlockForParseDateToIso(host);
    // CoreVariableBlocks.customBlockForCustomLoop(host);
    // CoreVariableBlocks.customBlockForRegexMatchString(host);
    // CoreVariableBlocks.customBlockForStringContains(host);
    // CoreVariableBlocks.customBlockForNumberToWords(host);
    CoreVariableBlocks.customBlockForSetVariable(host);
    CoreVariableBlocks.customBlockForGetVariable(host);
    CoreVariableBlocks.customBlockForSetAobVariable(host);
    CoreVariableBlocks.customBlockForGetAobVariable(host);
    // ╔══ DEPRECATED — legacy typed variable REGISTRATIONS (superseded by set_variable/
    //    get_variable above). Kept ONLY so saved functions whose xml references the old
    //    types still load. TO REMOVE the legacy blocks: comment out / delete this whole
    //    registration block AND the 8 method definitions (same DEPRECATED markers below),
    //    once a corpus pass confirms no saved function uses
    //    set_/get_{number,string,boolean,time_stamp}_variable.
    CoreVariableBlocks.customBlockForSetNumberVariable(host);
    CoreVariableBlocks.customBlockForGetNumberVariable(host);
    CoreVariableBlocks.customBlockForSetStringVariable(host);
    CoreVariableBlocks.customBlockForGetStringVariable(host);
    CoreVariableBlocks.customBlockForSetTimeStampVariable(host);
    CoreVariableBlocks.customBlockForGetTimeStampVariable(host);
    CoreVariableBlocks.customBlockForSetBooleanVariable(host);
    CoreVariableBlocks.customBlockForGetBooleanVariable(host);
    // ╚══ END DEPRECATED legacy typed variable REGISTRATIONS
    TimeStampFns.customBlockForCurrentDateISO();
    TimeStampFns.customBlockForDateAddDays();
    TimeStampFns.customBlockForDateDifference();
    TimeStampFns.customBlockForStartOfMonth();
    TimeStampFns.customBlockForEndOfMonth();
    TimeStampFns.customBlockForStartOfDay();
    TimeStampFns.customBlockForEndOfDay();
    TimeStampFns.customBlockForTimeDifference();
    TimeStampFns.customBlockForSetDate();
    TimeStampFns.customBlockForSetTime();
    ConditionalFns.customBlockForReturnBoolean();
    ValueFns.customBlockForReturnValue();
    // push_to_entity_list: deprecated, toolbox-hidden, but 225 saved uses -> must stay LOADABLE.
    EntityListPushFns.customBlockForPushToEntityList();
    QueryFns.customBlockForQuery();
    QueryFns.customBlockForSetOrderByVariable();
    QueryFns.customBlockForEntityConditionsForQuery();
    QueryFns.customBlockForOrConditionInQuery();
    QueryFns.customBlockForStringInConditionInQuery();
    QueryFns.customBlockForStringConditionsInQuery();
    QueryFns.customBlockForTimeStampInConditionInQuery();
    QueryFns.customBlockForTimeStampConditionsInQuery();
    QueryFns.customBlockForNumberInConditionInQuery();
    QueryFns.customBlockForNumberConditionsInQuery();
    QueryFns.customBlockForBooleanInConditionInQuery();
    QueryFns.customBlockForBooleanConditionsInQuery();
    QueryFns.customBlockForIdInConditionInQuery();
    QueryFns.customBlockForIdConditionsInQuery();
    QueryFns.customBlockForIsNullConditionsInQuery();
    QueryFns.customBlockForCreateEntityMutationVariable();
    QueryFns.customBlockForUpdateEntityMutationVariable();  
    QueryFns.customBlockForAsyncFunction();
    QueryFns.customBlockForResolvePromiseByGroup();
    QueryFns.customBlockForSelectEntityReference();
    // AAOB-CACHE-INVARIANT: filter cache query blocks. Sort block deferred — see
    // docs/plans/aaob-sort-revisit.md. The selection UI for these blocks lives
    // in the Angular HTML panel (allowedType === 'AAOB_FILTER'); see the
    // SELECTED handler and toBeAddedVaraibleChanged() below.
    QueryFns.customBlockForAAOBFilterByLabel();
    QueryFns.customBlockForAAOBLabelExists();
    QueryFns.customBlockForAAOBSortByLabel();
    // Phase 2 query-capabilities: aggregate query block (avg/sum/min/max/count
    // over a regular entity column). Selection UI lives in the AGGREGATE_ENTITY
    // HTML panel — see the SELECTED handler and toBeAddedVaraibleChanged() below.
    QueryFns.customBlockForEntityAggregateQuery();
    // Phase 2 query-capabilities (Phase D): AAOB-label aggregate block.
    // Uses the AAOB_AGGREGATE HTML panel (entity dropdown + AAOB field
    // dropdown scoped to that entity). systemKey is a free-text input.
    QueryFns.customBlockForAAOBAggregateByLabel();
    EntityVariableFns.customBlockForSetEntityVariable();
    EntityVariableFns.customBlockForGetEntityVariable();
    EntityVariableFns.customBlockForSetEntityFromEntityListVariable();
    EntityVariableFns.customBlockForSetReferenceEntityVariableFromArrayObjects();
    EntityVariableFns.customBlockForGetReferenceEntityVariableFromArrayObjectsVariableReferenceOfAOB();
    EntityVariableFns.customBlockForSetKeyValueInAobReference();
    // ╔══ DEPRECATED — legacy typed AOB-reference variable REGISTRATIONS (superseded by
    //    set_aob_variable / get_aob_variable). Kept ONLY so saved functions whose xml
    //    references the old types still load. SAFE TO REMOVE these 8 calls + their defs in
    //    helpers/entity-variable-healper.ts once a corpus pass confirms no saved function
    //    uses set_/get_reference_entity_variable_for_AOB_variable_*. (Structural AOB blocks
    //    above — set_reference_..._for_AOB / get_..._reference_AOB / set_key_value — STAY.)
    EntityVariableFns.customBlockForGetReferenceEntityVariableFromArrayObjectsVariableString();
    EntityVariableFns.customBlockForSetReferenceEntityVariableFromArrayObjectsVariableString();
    EntityVariableFns.customBlockForGetReferenceEntityVariableFromArrayObjectsVariableTimeStamp();
    EntityVariableFns.customBlockForSetReferenceEntityVariableFromArrayObjectsVariableTimeStamp();
    EntityVariableFns.customBlockForGetReferenceEntityVariableFromArrayObjectsVariableNumber();
    EntityVariableFns.customBlockForSetReferenceEntityVariableFromArrayObjectsVariableNumber();
    EntityVariableFns.customBlockForGetReferenceEntityVariableFromArrayObjectsVariableBoolean();
    EntityVariableFns.customBlockForSetReferenceEntityVariableFromArrayObjectsVariableBoolean();
    // ╚══ END DEPRECATED legacy typed AOB-reference variable REGISTRATIONS
    EntityVariableFns.customBlockForGetEntityListVariable();
    EntityVariableFns.customBlockForSetEntityListVariable();
    EntityVariableFns.customBlockForGetEntityLength();
    EntityVariableFns.customBlockForPushNewElementToArray();
    CoreVariableBlocks.customBlockForPushNewElementToArrayV2(host); // overrides the helper above: value-carrying push
    EntityVariableFns.customBlockForDeleteFromEntityList(); 
    SpecialFunctionsFns.customBlockForNavigation();
    SpecialFunctionsFns.customBlockForOpenExternalString();
    SpecialFunctionsFns.customBlockForGetEmail();
    SpecialFunctionsFns.customBlockForLogout();
    SpecialFunctionsFns.customBlockForGetCurrentUser();
    SpecialFunctionsFns.customBlockForGetCurrentUserRole();
    SpecialFunctionsFns.customBlockForShowToast(); // Add this line to initialize the toast block
    SpecialFunctionsFns.customBlockForGetUsers();
    SpecialFunctionsFns.customBlockForEditUser();
    SpecialFunctionsFns.customBlockForCreateUser();
    SpecialFunctionsFns.customBlockForGetRoles();
    SpecialFunctionsFns.customBlockForShowLoadingScreen();
    // System auth blocks — used on the sandboxed landingPage (Track C).
    SpecialFunctionsFns.customBlockForAuthLogin();
    SpecialFunctionsFns.customBlockForAuthSendPasswordReset();
    SpecialFunctionsFns.customBlockForAuthSignupFound();
    SpecialFunctionsFns.customBlockForAppVersion();
    // Tenant-admin blocks — authenticated pages only (full toolbox, never the sandbox).
    SpecialFunctionsFns.customBlockForTenantAddUser();
    SpecialFunctionsFns.customBlockForTenantCancelInvite();
    SpecialFunctionsFns.customBlockForTenantEditUserRole();
    SpecialFunctionsFns.customBlockForUserCreateAccount();
    SpecialFunctionsFns.customBlockForUserSetEmail();
    SpecialFunctionsFns.customBlockForUserSetUsername();
    SpecialFunctionsFns.customBlockForUserResetPassword();
    SpecialFunctionsFns.customBlockForUserChangePassword();
}
